/**
 * TipModal · iter137
 *
 * Trinkgeld an einen Creator senden.
 * Presets: 2 / 5 / 10 / 20 € · Freibetrag 1–500 €.
 * Optional: Nachricht (max 1000 Zeichen), "Öffentlich zeigen?"-Toggle.
 * Submit → POST /api/tips → Stripe-Checkout-Redirect.
 *
 * Props:
 *  - open, onClose
 *  - creator: { person_id, display_name, avatar_url }
 *  - source: "PROFILE" | "POST" | "CHAT"
 *  - sourceId?: string (post_id / thread_id)
 */
import { useState, useMemo, useEffect } from "react";
import { X, Heart, Loader2, Sparkles, CheckCircle } from "lucide-react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";

const PRESETS = [2, 5, 10, 20];

const METHOD_LABEL = {
  STRIPE: "Kreditkarte (Stripe)",
  PAYPAL: "PayPal",
  BANK_TRANSFER: "Überweisung / Vorkasse",
};

export default function TipModal({ open, onClose, creator, source, sourceId }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(5);
  const [customMode, setCustomMode] = useState(false);
  const [message, setMessage] = useState("");
  const [showPublic, setShowPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // iter264 · Zahlungsarten-Parität (gleiche aktive Methoden wie Admin/Checkout).
  const [methods, setMethods] = useState([]);
  const [method, setMethod] = useState(null);
  const [bankInfo, setBankInfo] = useState(null);
  const [done, setDone] = useState(false);

  const amountValid = useMemo(() => Number(amount) >= 1 && Number(amount) <= 500, [amount]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/payments/methods");
        const list = (data?.methods || data || []).filter((m) => m.enabled !== false);
        setMethods(list);
        setMethod((list[0] && list[0].code) || "STRIPE");
      } catch { setMethods([{ code: "STRIPE" }]); setMethod("STRIPE"); }
    })();
  }, [open]);

  if (!open || !creator) return null;

  const submit = async () => {
    setError(null); setBankInfo(null);
    if (!amountValid) { setError(t("tip.error_amount", { defaultValue: "Betrag muss zwischen 1 und 500 € liegen." })); return; }
    if (!method) { setError(t("tip.error_method", { defaultValue: "Bitte Zahlungsart wählen." })); return; }
    setBusy(true);
    try {
      const body = {
        creator_id: creator.person_id || creator._id || creator.id,
        amount_eur: Number(amount),
        source,
        source_id: sourceId || null,
        message_html: message || null,
        show_public: !!showPublic,
        origin_url: window.location.origin,
        payment_method: method,
      };
      const { data } = await api.post("/tips", body);
      if (data?.mock && data?.tip_id) {
        await api.post("/tips/mock-confirm", { tip_id: data.tip_id });
        setDone(true);
        return;
      }
      if (data?.bank_instructions) { setBankInfo(data.bank_instructions); return; }
      if (data?.url) { window.location.href = data.url; return; }
      onClose && onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) setError(t("tip.error_rate", { defaultValue: "Du hast heute die max. Tips für diesen Creator erreicht (10/Tag)." }));
      else if (e?.response?.status === 400) setError(detail || t("tip.error_self", { defaultValue: "Selbst-Tipping ist nicht erlaubt." }));
      else if (e?.response?.status === 409) setError(detail?.message || t("tip.error_method_inactive", { defaultValue: "Diese Zahlungsart ist aktuell deaktiviert." }));
      else setError((typeof detail === "string" ? detail : detail?.message) || t("tip.error_generic", { defaultValue: "Etwas ist schief gelaufen. Bitte später erneut versuchen." }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="tip-modal-backdrop"
      className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div data-testid="tip-modal" className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#F0F0F0]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center">
              <Heart size={18} className="text-pink-600 fill-pink-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-[#0A0A0A]">{t("tip.title", { defaultValue: "Trinkgeld senden" })}</h2>
              <p className="text-xs text-[#666]">{t("tip.subtitle", { defaultValue: "an {{name}}", name: creator.display_name || creator.handle || "Creator" })}</p>
            </div>
          </div>
          <button data-testid="tip-modal-close" onClick={onClose} className="p-2 rounded-full hover:bg-[#F5F5F5]">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Presets */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#666] mb-2 block">
              {t("tip.amount_label", { defaultValue: "Betrag" })}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRESETS.map((p) => {
                const active = !customMode && Number(amount) === p;
                return (
                  <button
                    key={p}
                    data-testid={`tip-preset-${p}`}
                    onClick={() => { setAmount(p); setCustomMode(false); }}
                    className={`h-12 rounded-2xl font-bold text-base transition-colors ${active ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#0A0A0A] hover:bg-[#EAEAEA]"}`}>
                    {p} €
                  </button>
                );
              })}
            </div>
            <button
              data-testid="tip-toggle-custom"
              onClick={() => setCustomMode((v) => !v)}
              className="text-xs text-[#666] underline mt-2 hover:text-[#0A0A0A]">
              {customMode ? t("tip.hide_custom", { defaultValue: "Preset wählen" }) : t("tip.show_custom", { defaultValue: "Freibetrag eingeben" })}
            </button>
            {customMode && (
              <input
                data-testid="tip-custom-amount"
                type="number"
                min={1} max={500} step={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-2 w-full h-12 rounded-full border border-[#E5E5E5] px-4 text-base font-bold focus:border-[#0A0A0A] focus:outline-none"
                placeholder={t("tip.custom_placeholder", { defaultValue: "Betrag in € (1–500)" })}
              />
            )}
          </div>

          {/* Message */}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-[#666] mb-2 block">
              {t("tip.message_label", { defaultValue: "Nachricht (optional)" })}
            </label>
            <textarea
              data-testid="tip-message"
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 1000))}
              rows={3}
              className="w-full rounded-2xl border border-[#E5E5E5] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none resize-none"
              placeholder={t("tip.message_placeholder", { defaultValue: "Persönliche Nachricht an den Creator …" })}
            />
            <p className="text-[10px] text-[#999] mt-1 text-right">{message.length}/1000</p>
          </div>

          {/* Public toggle */}
          <button
            data-testid="tip-toggle-public"
            onClick={() => setShowPublic((v) => !v)}
            className="w-full flex items-center gap-3 rounded-2xl border border-[#E5E5E5] p-3 text-left hover:bg-[#FAFAFA]">
            <div className={`w-11 h-6 rounded-full transition-colors ${showPublic ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"} relative`}>
              <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${showPublic ? "translate-x-5" : "translate-x-0.5"}`} />
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-[#0A0A0A]">{t("tip.public_title", { defaultValue: "Öffentlich zeigen" })}</p>
              <p className="text-xs text-[#666]">{t("tip.public_body", { defaultValue: "Dein Name + Betrag erscheint sichtbar unter dem Post/Profil." })}</p>
            </div>
          </button>

          {/* iter264 · Zahlungsart-Auswahl (gleiche aktive Methoden wie Checkout) */}
          <div data-testid="tip-methods">
            <label className="text-xs font-bold uppercase tracking-wider text-[#666] mb-2 block">
              {t("tip.method_label", { defaultValue: "Zahlungsart" })}
            </label>
            <div className="flex flex-col gap-2">
              {methods.map((m) => (
                <button
                  key={m.code}
                  data-testid={`tip-method-${m.code}`}
                  onClick={() => setMethod(m.code)}
                  className={`h-11 rounded-2xl border px-4 text-sm font-semibold text-left transition-colors ${method === m.code ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]"}`}>
                  {METHOD_LABEL[m.code] || m.label || m.code}
                </button>
              ))}
            </div>
          </div>

          {/* Bank-Anweisungen (Vorkasse) */}
          {bankInfo && (
            <div data-testid="tip-bank-info" className="rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] p-3 text-xs text-[#333] space-y-1">
              <p className="font-semibold text-[#0A0A0A]">{t("tip.bank_title", { defaultValue: "Überweisungsdaten" })}</p>
              <p>IBAN: <strong>{bankInfo.iban || "—"}</strong></p>
              <p>BIC: <strong>{bankInfo.bic || "—"}</strong></p>
              <p>{t("tip.bank_reference", { defaultValue: "Verwendungszweck" })}: <strong>{bankInfo.reference}</strong></p>
              <p>{t("tip.bank_amount", { defaultValue: "Betrag" })}: <strong>{Number(bankInfo.amount).toFixed(2)} €</strong></p>
              <p className="text-[#999]">{t("tip.bank_note", { defaultValue: "Das Trinkgeld wird nach Zahlungseingang gutgeschrieben." })}</p>
            </div>
          )}

          {/* Erfolg */}
          {done && (
            <div data-testid="tip-success" className="rounded-2xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle size={16} /> {t("tip.success", { defaultValue: "Trinkgeld gesendet — vielen Dank!" })}
            </div>
          )}

          {/* Error */}
          {error && (
            <div data-testid="tip-error" className="rounded-2xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-[#F0F0F0] flex flex-col gap-2">
          {done ? (
            <button
              data-testid="tip-close-success"
              onClick={onClose}
              className="w-full h-12 rounded-full bg-[#0A0A0A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#222]">
              {t("common.close", { defaultValue: "Schließen" })}
            </button>
          ) : (
            <button
              data-testid="tip-submit"
              disabled={busy || !amountValid || !method}
              onClick={submit}
              className="w-full h-12 rounded-full bg-[#0A0A0A] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-40 hover:bg-[#222]">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
              {method === "BANK_TRANSFER"
                ? t("tip.submit_bank", { defaultValue: "{{amount}} € per Überweisung", amount: amountValid ? Number(amount).toFixed(2) : "0" })
                : t("tip.submit", { defaultValue: "{{amount}} € Trinkgeld senden", amount: amountValid ? Number(amount).toFixed(2) : "0" })}
            </button>
          )}
          <p className="text-[10px] text-[#999] text-center">
            {t("tip.info_secure", { defaultValue: "Sichere Zahlung · nicht rückerstattbar." })}
          </p>
        </div>
      </div>
    </div>
  );
}
